import { Global, css } from '@emotion/react';
import type { CenterProps } from '@hover/blueprint';
import {
  Box,
  Center,
  Heading,
  Image,
  forwardRef,
  motion,
  transition,
  useBoolean,
  useTheme,
} from '@hover/blueprint';
import { AnimatePresence } from 'framer-motion';
import React, { useCallback, useState } from 'react';

import { altStyle } from 'src/modules/homeownerApp/helpers/styles';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import { useHomeStyles } from '../HomeStyles';

import { StyleArrow } from './StyleArrow';
import { StylePager } from './StylePager';

export interface StyleImageProps extends Omit<CenterProps, 'children'> {
  aspectRatio?: string;
}

const MotionBox = motion(Box);

const StyleImage = forwardRef<StyleImageProps, 'div'>(
  ({ aspectRatio = { base: '21/9', tablet: '2048/1502' }, ...props }, ref) => {
    const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);

    const [isPlaying, { on: play, off: pause, toggle }] = useBoolean(true);
    const [isTouching, setIsTouching] = useState(false);

    const { colors } = useTheme();
    const { aspect, currentStyle, getTabId, next, previous } = useHomeStyles();
    const { name } = currentStyle;

    const styleName = t(`home.styles.${name}`);

    const onHold = useCallback(() => {
      pause();
      setIsTouching(true);
    }, [pause]);

    const onRelease = useCallback(() => {
      play();
      setIsTouching(false);
    }, [play]);

    return (
      <Center
        alignItems="start"
        data-group
        onMouseEnter={pause}
        onMouseLeave={play}
        onTouchCancel={onRelease}
        onTouchEnd={onRelease}
        onTouchStart={onHold}
        overflow="hidden"
        position="relative"
        {...props}
        ref={ref}
      >
        <Global
          styles={css`
            body {
              user-select: ${isTouching ? 'none' : 'initial'};
            }
          `}
        />
        <AnimatePresence initial={false} mode="popLayout">
          <MotionBox
            animate={{ opacity: 1 }}
            borderRadius={{ base: '0', tablet: '200' }}
            exit={{ opacity: 0 }}
            height="100%"
            initial={{ opacity: 0 }}
            key={`${aspect}-${name}`}
            overflow="hidden"
            position="relative"
            transition={{ duration: 0.5 }}
            width={{ base: 'auto', tablet: '100%' }}
          >
            <Image
              _groupHover={{ filter: 'brightness(0.8)' }}
              alt={`${styleName} ${t(`home.aspects.${aspect}`)}`}
              height="100%"
              id={getTabId(currentStyle)}
              objectFit="cover"
              pointerEvents="none"
              src={currentStyle[aspect].image}
              sx={{ aspectRatio }}
              width="100%"
              {...altStyle()}
              {...transition.easing250('filter')}
            />
            <Heading
              bottom={{ base: '600', tablet: '700' }}
              color="neutral.0"
              isLight
              left="600"
              lineHeight="200"
              mb="0"
              position="absolute"
              size={200}
              textShadow={`0 0 4px ${colors.darkAlpha[500]}`}
            >
              {styleName}
            </Heading>
          </MotionBox>
        </AnimatePresence>

        <StyleArrow
          aria-label={t('home.previousStyle')}
          direction="previous"
          left="600"
          onClick={previous}
          position="absolute"
          top="50%"
        />
        <StyleArrow
          aria-label={t('home.nextStyle')}
          direction="next"
          onClick={next}
          position="absolute"
          right="600"
          top="50%"
        />

        <StylePager
          bottom={{ base: '600', tablet: '700' }}
          isPlaying={isPlaying}
          onToggle={toggle}
          position="absolute"
          right="600"
        />
      </Center>
    );
  },
);

export { StyleImage };
